import { useEffect, useState } from 'react';
import { CheckCircle, X, Loader2 } from 'lucide-react';
import { pollCheckoutSuccessStatus } from '../../lib/billing';
import { signInWithIdentifier } from '../../lib/auth';

type PaymentSuccessModalProps = {
  email?: string;
  password?: string;
  onClose: () => void;
  onSuccessRedirect?: () => void;
};

export default function PaymentSuccessModal({ email, password, onClose, onSuccessRedirect }: PaymentSuccessModalProps) {
  const [isReady, setIsReady] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => {
    // If no email provided, assume this is a post-checkout-confirmed flow
    // and show success immediately without polling.
    if (!email) {
      setIsReady(true);
      return;
    }

    let mounted = true;
    let attempts = 0;
    const maxAttempts = 15; // 15 * 2s = 30 seconds max polling

    const poll = async () => {
      try {
        const ready = await pollCheckoutSuccessStatus(email);
        if (!mounted) return;

        if (ready) {
          setIsReady(true);
          // Optional: automatically log the user in if we have the password
          if (password && onSuccessRedirect) {
            setIsLoggingIn(true);
            try {
              const res = await signInWithIdentifier(email, password);
              if (res?.session) {
                onSuccessRedirect();
                return;
              }
            } catch (err) {
              console.error('Auto sign-in failed', err);
            } finally {
              if (mounted) setIsLoggingIn(false);
            }
          }
          return;
        }

        attempts++;
        if (attempts >= maxAttempts) {
          setIsFailed(true);
          return;
        }

        setTimeout(poll, 2000);
      } catch {
        if (!mounted) return;
        attempts++;
        if (attempts >= maxAttempts) {
          setIsFailed(true);
          return;
        }
        setTimeout(poll, 2000);
      }
    };

    poll();

    return () => {
      mounted = false;
    };
  }, [email, password, onSuccessRedirect]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="relative w-full max-w-sm overflow-hidden rounded-2xl border border-slate-700/40 bg-slate-900 p-6 shadow-2xl backdrop-blur-sm animate-in fade-in zoom-in-95 duration-200">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"
        >
          <X size={20} />
        </button>

        <div className="flex flex-col items-center text-center">
          {isReady ? (
            <div className="mb-4 rounded-full bg-emerald-500/20 p-3">
              <CheckCircle className="h-10 w-10 text-emerald-400" />
            </div>
          ) : isFailed ? (
            <div className="mb-4 rounded-full bg-red-500/20 p-3">
              <X className="h-10 w-10 text-red-400" />
            </div>
          ) : (
            <div className="mb-4 rounded-full bg-sky-500/20 p-3">
              <Loader2 className="h-10 w-10 animate-spin text-sky-400" />
            </div>
          )}

          <h3 className="mb-2 text-xl font-semibold text-white">
            {isReady ? "Payment successful" : isFailed ? "Activation Delayed" : "Setting up your account..."}
          </h3>

          <p className="mb-6 text-sm text-slate-300">
            {isReady
              ? isLoggingIn
                ? "Signing you in..."
                : "Your transaction is complete. Your account is now fully active and ready to use."
              : isFailed
              ? "Your payment was successful, but account creation is taking longer than expected. Please check your email or try signing in shortly."
              : "We're finalizing your account creation. Please hold on a moment."}
          </p>

          <button
            onClick={() => {
              if (isReady && !isLoggingIn) {
                if (onSuccessRedirect) {
                  onSuccessRedirect();
                } else {
                  onClose();
                }
              } else if (isFailed) {
                onClose();
              }
            }}
            disabled={!isReady && !isFailed}
            className="w-full rounded-lg bg-emerald-500 py-2.5 font-medium text-white transition-colors hover:bg-emerald-600 disabled:opacity-50"
          >
            {isReady ? (isLoggingIn ? "Loading..." : "Continue to the dashboard") : isFailed ? "Close" : "Please wait..."}
          </button>
        </div>
      </div>
    </div>
  );
}
